import * as Sentry from '@sentry/react'

const SENTRY_SESSION_ID_KEY = 'beoflow_sentry_session_id'

/** localStorage key holding the signed-in person's backend `profile.id`.
 *  Written by AuthContext once the profile resolves, removed by `clearTokens`,
 *  so it is present exactly while a session is. Exported so the two readers
 *  cannot drift apart on a string literal. */
export const PROFILE_ID_STORAGE_KEY = 'profile_id'

/** profile.id from the current session, if one has already been stored. Lets an
 *  error thrown BEFORE the profile fetch resolves — a boot crash, a render error
 *  the ErrorBoundary catches on first paint — still be attributed. */
function storedProfileId(): string | null {
  try {
    return window.localStorage.getItem(PROFILE_ID_STORAGE_KEY)
  } catch {
    return null
  }
}

const ignoredMessages = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  'Network Error',
  'AbortError',
]

export function getSentrySessionId() {
  if (typeof window === 'undefined') return 'server'
  try {
    const existing = window.sessionStorage.getItem(SENTRY_SESSION_ID_KEY)
    if (existing) return existing
    const id = globalThis.crypto?.randomUUID?.() ?? `bfw_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`
    window.sessionStorage.setItem(SENTRY_SESSION_ID_KEY, id)
    return id
  } catch {
    return 'unavailable'
  }
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  const sessionId = getSentrySessionId()
  const profileId = storedProfileId()

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
    initialScope: {
      tags: { session_id: sessionId },
      ...(profileId ? { user: { id: profileId } } : {}),
    },
    beforeSend(event, hint) {
      const message = hint.originalException instanceof Error
        ? hint.originalException.message
        : event.message ?? ''
      if (ignoredMessages.some((ignored) => message.includes(ignored))) return null
      return event
    },
  })
  Sentry.setTag('session_id', sessionId)
}

/**
 * Attribute events to the person by the opaque backend `profile.id`, and nothing
 * else. That is the same handle PostHog's distinct_id and the backend's structlog
 * `profile_id` carry, so one browser error can be joined to its server-side
 * request and a fix verified against the person who reported it.
 *
 * profile.id ONLY — no email, name or `auth.User.id`:
 *  - email/name are PII. `user.email` is precisely the field Sentry's
 *    `send_default_pii` exists to keep out, and it was what this function sent
 *    before, so every webapp event carried a live address.
 *  - `auth.User.id` survives profile-scoped GDPR erasure (backend
 *    `core.analytics.delete_person` deletes by profile.id), so a Sentry user keyed
 *    on it outlives the deletion meant to remove that person. Same reasoning as
 *    `identifyPostHogUser`, and deliberately with the same no-fallback rule.
 *
 * With no profile the user is CLEARED, never identified some other way — an
 * unattributed event is strictly better than a wrongly-attributed one.
 */
export function setSentryUser(user: { profile_id?: number | null } | null | undefined) {
  const profileId = user?.profile_id
  Sentry.setUser(profileId == null ? null : { id: String(profileId) })
}

export { Sentry }
